import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { FaLinkedinIn, FaBehance, FaInstagram } from "react-icons/fa";
import { useLang } from "../i18n/LanguageContext";
import { openContactForm } from "../lib/contactModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [s, setS] = useState({
    contact_email: "tico@studionuts.com.br",
    linkedin_url: "https://www.linkedin.com/in/ticomoraes/",
    behance_url: "https://www.behance.net/studionuts",
    instagram_url: "https://www.instagram.com/studio.nuts/",
    availability_en: "",
    availability_pt: "",
  });

  useEffect(() => {
    fetch(`${API}/settings`)
      .then((r) => r.json())
      .then((d) => setS((prev) => ({ ...prev, ...d })))
      .catch(() => {});
  }, []);

  const mailto = `mailto:${s.contact_email}?subject=New%20Project%20Inquiry&body=Hi%20Tico%2C%0A%0AI%27d%20love%20to%20chat%20about%20a%20project...`;
  const availability = lang === "pt" ? s.availability_pt || s.availability_en : s.availability_en;

  const socials = [
    { label: "LinkedIn", href: s.linkedin_url, Icon: FaLinkedinIn, testid: "social-linkedin" },
    { label: "Behance", href: s.behance_url, Icon: FaBehance, testid: "social-behance" },
    { label: "Instagram", href: s.instagram_url, Icon: FaInstagram, testid: "social-instagram" },
  ].filter((x) => x.href);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-40 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[80vw] h-[80vw] rounded-full opacity-[0.12] blur-[160px]"
        style={{ background: "#ff4500" }}
      />

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex items-center gap-4 mb-10 md:mb-14">
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff4500]">
            {t.contact.label}
          </span>
          <span className="h-[1px] w-16 bg-[#262626]" />
        </div>

        <h2
          data-testid="contact-heading"
          className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[9vw] font-light leading-[0.95] tracking-tighter max-w-5xl"
        >
          {t.contact.headingA} <span className="italic text-[#ff4500]">{t.contact.headingB}</span>
          {t.contact.headingC}
          <br />
          {t.contact.headingD} <span className="italic">{t.contact.headingE}</span>.
        </h2>

        <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
              {t.contact.dropLine}
            </div>
            <a
              href={mailto}
              data-testid="contact-email-link"
              className="group inline-flex items-center gap-4 font-serif text-3xl sm:text-4xl md:text-5xl font-light text-white hover:text-[#ff4500] transition-colors duration-500 break-all"
            >
              <span className="link-underline">{s.contact_email}</span>
              <ArrowUpRight
                size={32}
                className="text-[#ff4500] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300 flex-shrink-0"
              />
            </a>

            <div role="group" aria-label="Contact actions" className="mt-12 flex flex-col sm:flex-row gap-4 sm:items-center">
              <button
                type="button"
                onClick={openContactForm}
                data-testid="contact-primary-cta"
                aria-label={t.contact.primaryCta}
                className="btn-orange inline-flex items-center justify-center px-8 py-5 text-[12px] uppercase tracking-[0.25em] font-medium w-fit"
              >
                <span>{t.contact.primaryCta}</span>
              </button>
              {s.linkedin_url && (
                <a
                  href={s.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin-cta"
                  aria-label={t.contact.linkedinCta}
                  className="btn-outline-orange inline-flex items-center gap-3 px-8 py-5 text-[12px] uppercase tracking-[0.25em] font-medium w-fit"
                >
                  <span>{t.contact.linkedinCta}</span>
                  <ArrowUpRight size={14} />
                </a>
              )}
            </div>
          </div>

          <div className="lg:col-span-4 lg:col-start-9 flex flex-col gap-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
                {t.contact.studios}
              </div>
              <p className="text-white text-lg leading-relaxed">
                Lisbon, Portugal
                <br />
                São Paulo, Brazil
                <br />
                Dubai, UAE
              </p>
            </div>

            {socials.length > 0 && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
                  {t.contact.elsewhere}
                </div>
                <div className="flex items-center gap-4">
                  {socials.map(({ label, href, Icon, testid }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={testid}
                      aria-label={label}
                      className="w-11 h-11 border border-[#262626] flex items-center justify-center text-neutral-400 hover:border-[#ff4500] hover:text-[#ff4500] hover:bg-[#ff4500]/5 transition-all duration-300"
                    >
                      <Icon size={15} />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {availability && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
                  {t.contact.availability}
                </div>
                <p className="text-neutral-400 leading-relaxed">{availability}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
